/**
 * useHostScope — host-aware scope resolver (F7b T7b.1).
 *
 * Zwraca aktywny scope SPA na podstawie `window.location.hostname`. Logika spójna
 * z backend `resolveHost()` w `apps/api/lib/host-resolver.ts` (per-route, F15a, ADR-0009):
 *  - `admin.*` → `admin` (super-admin panel: F6/F7a).
 *  - `app.*` exact subdomain (bez dalszych) → `public` (Register, PublicLogin w F7b).
 *  - `*.app.*` (dowolny slug przed `app.`) → `tenant` (tenant shell w F7b).
 *  - fallback → `unknown` (np. user wpisał `kominek.wfnew-dev.ite.pl` zamiast
 *    `kominek.app.wfnew-dev.ite.pl`). App renderuje `<HostMisconfigured />`,
 *    żeby nie pokazywać przypadkowo admin/tenant panelu na złym URL.
 *    Wcześniej (do F12.N D1, 2026-05-15) fallback zwracał `admin` — niebezpieczny UX
 *    bo super-admin mógł zalogować się na nieistniejącym subdomenie i operować
 *    jak na panelu ITE.
 *
 * DEV override (żeby testować scope na `localhost:5173` bez DNS/hosts file):
 *  1. Query param `?hostScope=admin|public|tenant` — najwyższy priorytet (cross-session
 *     testing, reload strony trzyma param).
 *  2. `VITE_HOST_SCOPE_DEV` env — fallback gdy brak query param (fixed per `.env.local`).
 *  3. Hostname match (produkcja).
 *
 * Oba overrides działają tylko w DEV (`import.meta.env.DEV === true`). W prod
 * hostname jest zawsze source-of-truth, żeby nie można było wymusić scope manipulacją URL-a.
 */

import { useMemo } from 'react';

export type HostScope = 'admin' | 'public' | 'tenant' | 'unknown';

type DevOverrideScope = Exclude<HostScope, 'unknown'>;

const VALID_DEV_SCOPES: readonly DevOverrideScope[] = ['admin', 'public', 'tenant'];

function isValidDevScope(value: string | null | undefined): value is DevOverrideScope {
  return typeof value === 'string' && (VALID_DEV_SCOPES as readonly string[]).includes(value);
}

/**
 * Resolwer testowalny bez React. `useHostScope` jest cienkim wrapper-em z memo.
 * Eksportowany żeby interceptor w `api.ts` mógł wywołać tę samą logikę poza
 * drzewem React (axios interceptor żyje na module level).
 */
const DEV_SCOPE_KEY = '__devHostScope';

export function resolveHostScope(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): HostScope {
  const isDev = Boolean(import.meta.env?.DEV);

  if (isDev) {
    // Query param override — najwyższy priorytet; persist do sessionStorage.
    if (search) {
      try {
        const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
        const q = params.get('hostScope');
        if (isValidDevScope(q)) {
          try {
            sessionStorage.setItem(DEV_SCOPE_KEY, q);
          } catch {
            /* prywatny tryb */
          }
          return q;
        }
      } catch {
        // malformed query — ignore, fall through.
      }
    }
    // sessionStorage — persist scope między stronami w tej samej karcie.
    try {
      const stored = sessionStorage.getItem(DEV_SCOPE_KEY);
      if (isValidDevScope(stored)) return stored;
    } catch {
      /* prywatny tryb */
    }
    // ENV override.
    const envScope = import.meta.env?.VITE_HOST_SCOPE_DEV;
    if (isValidDevScope(envScope)) return envScope;
  }

  // Produkcja / DEV bez override: hostname-based matching.
  const host = hostname.toLowerCase();

  // admin.naszsaas.pl, admin.example.com, admin.localhost
  if (/^admin(\.|$)/.test(host)) return 'admin';

  // app.naszsaas.pl (exact public subdomain, bez slugu przed)
  // albo <slug>.app.naszsaas.pl (tenant subdomain)
  const appMatch = host.match(/^([^.]+)\.app\.(.+)$/);
  if (appMatch) return 'tenant';

  if (/^app(\.|$)/.test(host)) return 'public';

  // Fallback — nieznany hostname. App renderuje `<HostMisconfigured />`
  // zamiast którejś z 3 sub-apps (admin/tenant/public). Zmiana z `admin` → `unknown`
  // wprowadzona w F12.N D1 (2026-05-15) — patrz JSDoc na górze pliku.
  return 'unknown';
}

export function useHostScope(): HostScope {
  return useMemo(() => resolveHostScope(), []);
}
